import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { useColorStore } from '@atlassian/eoc-color-store';
import { ResponderAvatar, useResponderNames } from '@atlassian/eoc-responder-names';

import { filterNoOne, getResponder } from '../../../utils/responder';
import { computePosition, formatAccessibleRange } from '../../../utils/shift';
import { messages as commonMessages } from '../../messages';

import { deletedColor, noOneColor } from './constants';
import {
	Background,
	Gradient,
	Inner,
	MissingResponder,
	Name,
	Outer,
	Overlay,
	OverrideBorder,
	Profile,
	Time,
	User,
} from './styled';
import type { SimpleShiftTileProps } from './types';
import { findLayout, formatName, formatTimeRange } from './utils';

const SimpleShiftTile = forwardRef<HTMLDivElement, SimpleShiftTileProps>(
	(
		{
			size,
			period,
			outerTimeFrame,
			hasOverrideBorder,
			onClick,
			onMouseEnter,
			isTabbable = false,
			isSelected = false,
			...rest
		},
		ref,
	) => {
		const { responder } = period;

		const intlApi = useIntl();

		const internalRef = useRef<HTMLDivElement>(null);
		useImperativeHandle(ref, () => internalRef.current as HTMLDivElement);

		const users = useResponderNames(filterNoOne([responder]));
		const user = users[0];

		const { getColor } = useColorStore();

		const { itemWidth, itemLeft, nowLocation, nowFillRatio } = computePosition(
			period,
			outerTimeFrame,
		);

		const layout = findLayout(itemWidth, size);

		// TODO: handle error and loading. See: TARDIS-7446
		const { name, type, deleted } = getResponder(responder, user, intlApi);

		const isNoOne = type === 'noone';
		const isDeleted = deleted;
		const userHasAvatar = user && 'avatarUrl' in user;

		const { color, backgroundColor } = isNoOne
			? noOneColor
			: isDeleted
				? deletedColor
				: getColor(responder.id);

		// TODO: Update label content. See: TARDIS-8571
		const label = name + ' ' + formatAccessibleRange(intlApi, period);

		return (
			<Outer size={size} left={itemLeft} width={itemWidth}>
				<Inner
					{...rest}
					color={color}
					hasBorder={isSelected}
					hasDashedBorder={isNoOne}
					isTrueStartTime={period.isTrueStartTime}
					isTrueEndTime={period.isTrueEndTime}
					size={size}
					layout={layout}
					data-testid="period-item"
					data-shift-id={period.id}
					ref={internalRef}
					tabIndex={isTabbable ? 0 : -1}
					role="button"
					aria-roledescription="shift"
					aria-haspopup="dialog"
					aria-label={label}
					onClick={onClick}
					onMouseEnter={onMouseEnter}
					onKeyPress={(event) => {
						// NOTE: Might want to exclude 'Spacebar' as it is also mean to select for multi mode
						if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
							event.preventDefault();
							onClick?.(event);
						}
					}}
				>
					{isDeleted ? <Gradient /> : <Background color={backgroundColor} />}

					{hasOverrideBorder && <OverrideBorder mini={isSelected} />}

					<User aria-hidden layout={layout}>
						{!(isNoOne || isDeleted) && (
							<Profile layout={layout} hasSvg={!userHasAvatar}>
								<ResponderAvatar
									appearance="circle"
									borderColor="transparent"
									responder={user}
									size="xsmall"
									testId="avatar"
								/>
							</Profile>
						)}
						{isDeleted && (
							<MissingResponder layout={layout}>
								<ErrorIcon
									color="currentColor"
									LEGACY_size="medium"
									spacing="spacious"
									label={intlApi.formatMessage(commonMessages.missingResponder)}
								/>
							</MissingResponder>
						)}
						<Name layout={layout}>{formatName(name, layout, isNoOne, isDeleted)}</Name>
					</User>

					<Time aria-hidden layout={layout}>
						{formatTimeRange(intlApi, period)}
					</Time>

					<Overlay
						isTrueStartTime={period.isTrueStartTime}
						isTrueEndTime={period.isTrueEndTime}
						nowFillRatio={nowFillRatio}
						nowLocation={nowLocation}
					/>
				</Inner>
			</Outer>
		);
	},
);

export { SimpleShiftTile };
